import { useEffect, useMemo, useRef, useState } from 'react';
import useLocales from 'src/hooks/useLocales';

// @mui
import {
  Box,
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
  Grid,
  Stack,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TableCell,
  Autocomplete,
  Chip,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';

import { observer } from 'mobx-react-lite';

import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';

import useSettings from 'src/hooks/useSettings';

import { PATH_DASHBOARD } from 'src/routes/paths';
import Page from 'src/components/Page';
import { useStore } from 'src/stores/store';
import { IRentPaymentReport } from 'src/@types/Choqorak/MainTables/RentPayment.ts/RentPayment';

import { Controller, useForm } from 'react-hook-form';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import Loader from 'src/components/loader/Loader';

import { GetMonthName } from 'src/sections/common/GetMonthName';
import { DateConverter } from 'src/sections/common/DateConverter';
import Label from 'src/components/Label';
import RentPaymentDetailsToolbar from './RentPaymentDetailsToolbar';
import * as XLSX from 'xlsx';
import { Icon } from '@iconify/react';
import { themeMode } from 'src/utils/general';
import { GetCurrencySymbol } from 'src/sections/common/GetCurrencySymbol';

// ----------------------------------------------------------------------
export default observer(function RentPaymentReportIndex() {
  const { RentPaymentStore, ChoqorakCommonDropdown } = useStore();
  const { translate } = useLocales();
  const [checked, setChecked] = useState(false);
  const { loadRentPaymentReport, loadRentPaymentDailyReport, RentpaymentReportResult } =
    RentPaymentStore;
  const { loadBlockeDDL, BlockOption, loadFloorDDL, FloorOption, loadShopDDL, ShopOption, loadCustomerDDL,
    CustomerOption } =
    ChoqorakCommonDropdown;

  const { themeStretch } = useSettings();
  const [isloading, setIsloading] = useState(false);
  const [fromYear, setFromYear] = useState<string>(''); // State for the year
  const [toYear, setToYear] = useState<string>(''); // State for the year
  const [fromMonth, setFromMonth] = useState<string>(''); // State for the month
  const [toMonth, setToMonth] = useState<string>(''); // State for the month
  const [block, setBlock] = useState<any>();
  const [floor, setFloor] = useState<any>();
  const [shop, setShop] = useState<any>();
  const [customer, setCustomer] = useState();
  const defaultValues = useMemo<IRentPaymentReport>(
    () => ({
      fromYear: new Date(),
      toYear: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
    }),
    []
  );
  const methods = useForm<IRentPaymentReport>({
    defaultValues,
  });
  const { watch, control } = methods;
  const val = watch();
  const fromDate = watch('fromYear'); // Watching the 'years' field
  const toDate = watch('toYear'); // Watching the 'years' field

  useEffect(() => {
    loadBlockeDDL();
    loadFloorDDL();
    loadCustomerDDL();
    if (floor) {
      loadShopDDL(floor, true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block, shop, floor]);

  useEffect(() => {
    if (fromDate) {
      const changeDateToDateFormat = new Date(fromDate);

      // Extract Persian year and month
      const persianYear = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        year: 'numeric',
      });
      const persianMonth = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        month: 'numeric',
      });

      // Convert Persian numerals to Western numerals
      const westernYear = persianYear.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );
      const westernMonth = persianMonth.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );

      // Update state with Western year and month
      setFromYear(westernYear);
      setFromMonth(westernMonth);
    }
    if (toDate) {
      const changeDateToDateFormat = new Date(toDate);

      // Extract Persian year and month
      const persianYear = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        year: 'numeric',
      });
      const persianMonth = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        month: 'numeric',
      });

      // Convert Persian numerals to Western numerals
      const westernYear = persianYear.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );
      const westernMonth = persianMonth.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );

      // Update state with Western year and month
      setToYear(westernYear);
      setToMonth(westernMonth);
    }
  }, [fromDate, toDate]);

  const Search = async (e: any) => {
    e.preventDefault();
    setIsloading(true);
    if (!checked) {
      loadRentPaymentReport({
        fromYear: Number(fromYear),
        fromMonth: Number(fromMonth),
        toYear: Number(toYear),
        toMonth: Number(toMonth),
        customerId: customer,
        blockId: block,
        floorId: floor,
        shopId: shop,
      })
        .then((res) => {
          setIsloading(false);
        })
        .catch((err) => {
          setIsloading(false);
        });
    } else {
      loadRentPaymentDailyReport({
        paymentDate: new Date(val.paymentDate!),
        blockId: block,
        floorId: floor,
        shopId: shop,
      })
        .then((res) => {
          setIsloading(false);
        })
        .catch((err) => {
          setIsloading(false);
        });
    }
  };

  const exportToExcel = () => {
    const data =
      RentpaymentReportResult?.list?.map((row, index) => ({
        No: index + 1,
        ContractId: row.contractId,
        Year: row.year,
        Month: row.month,
        ContractNumber: row.contractNumber || '-',
        CustomerName: row.customerName || '-',
        CustomerPhone: row.customerPhone || '-',
        ShopCode: row.shopCode || '-',
        contractStartDate: row.contractStartDate ? new Date(row.contractStartDate).toLocaleDateString('fa-IR') : '-',
        ContractEndDate: row.contractEndDate ? new Date(row.contractEndDate).toLocaleDateString('fa-IR') : '-',
        remnantsOfThePast: row.remnantsOfThePast || '-',
        currentMonthRent: row.currentMonthRent || '-',
        PaymentAmount: row.paymentAmount ?? '-',
        PaidAmount: row.paidAmount ?? '-',
        todaysRecievedAmount: row.todaysRecievedAmount ?? '-',
        RecievedByUserName: row.recievedByUserName || '-',
        RemainAmount: row.remainAmount ?? '-',
        PaymentDate: row.paymentDate ? new Date(row.paymentDate).toLocaleDateString('fa-IR') : '-',
        Description: row.description || '-',
      })) || [];



    if (data.length === 0) {
      alert('No data available to export.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rent Payment Report');

    XLSX.writeFile(workbook, 'RentPayment_Report.xlsx');
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };
  const flatReport = RentpaymentReportResult?.report ? RentpaymentReportResult.report.flat() : [];

  const groupedData = flatReport.reduce((acc, item) => {
    // Group by Year
    if (!acc[item.year]) {
      acc[item.year] = {};
    }

    // Group by Month within Year
    if (!acc[item.year][item.month]) {
      acc[item.year][item.month] = [];
    }

    // Add Currency Data
    acc[item.year][item.month].push({
      currencyId: item.currencyId,
      currencyName: item.currencyName,
      totalPaymentAmount: item.totalPaymentAmount,
      totalPaidAmount: item.totalPaidAmount,
      totalRemainAmount: item.totalRemainAmount,
    });

    return acc;
  }, {} as Record<number, Record<number, any[]>>);

  return (
    <Page title={translate('RentPayment.Title')}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={translate('RentPayment.RentPaymentReport')}
          links={[
            { name: `${translate('Department.Dashboard')}`, href: PATH_DASHBOARD.root },

            { name: `${translate('RentPayment.RentPaymentReport')}` },
          ]}
          action={<></>}
        />
        <Box
          sx={{ display: 'flex', alignItems: 'left', justifyContent: 'left' }} // Use justifyContent instead of justifyItems
        >
          <IconButton
            sx={{ color: themeMode() === 'light' ? 'black' : 'white' }}
            onClick={exportToExcel}
          >
            <Icon icon="vscode-icons:file-type-excel" width={24} height={24} />
          </IconButton>

          <RentPaymentDetailsToolbar
            checked={checked}
            fromYear={Number(fromYear)}
            fromMonth={Number(fromMonth)}
            toYear={Number(toYear)}
            toMonth={Number(toMonth)}
            paymentDate={val.paymentDate!}
            Report={RentpaymentReportResult} />
        </Box>

        <Grid
          container
          display="flex"
          alignItems="center"
          justifyContent="center" // Use justifyContent instead of justifyItems
        >
          <Grid item xs={12} md={12} spacing={2}>
            <Card
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center', // Use justifyContent instead of justifyItems
              }}
            >
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(6, 1fr)' },
                  }}
                >
                  {!checked ? (
                    <>
                      <LocalizDatePicker
                        name="fromYear"
                        label={translate('Report.FromDate')}
                        control={control}
                        size={'small'}
                        views={['year', 'month']}
                      />

                      <LocalizDatePicker
                        name="toYear"
                        label={translate('Report.ToDate')}
                        control={control}
                        size={'small'}
                        views={['year', 'month']}
                      />
                    </>
                  ) : (
                    <LocalizDatePicker
                      name="paymentDate"
                      label={translate('RentPayment.PaymentDate')}
                      control={control}
                      size={'small'}
                    />
                  )}
                  <Controller
                    name="blockId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        // onChange={(event, newValue) => setBlock(newValue.value)}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            field.onChange(newValue);
                            setBlock(newValue.value);
                          } else {
                            field.onChange(null);
                            setBlock(undefined);
                          }
                        }}
                        options={BlockOption.map((option: any) => option)}
                        value={field.value}
                        getOptionLabel={(option) => option.text}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              size="small"
                              label={option.value}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('Block.Block')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="floorId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        // onChange={(event, newValue) => setFloor(newValue.value)}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            field.onChange(newValue);
                            setFloor(newValue.value);
                          } else {
                            field.onChange(null);
                            setFloor(undefined);
                          }
                        }}
                        options={FloorOption.map((option: any) => option)}
                        value={field.value}
                        getOptionLabel={(option) => option.text}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              size="small"
                              label={option.value}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('Floor.Floor')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="customerId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        // onChange={(event, newValue) => setCustomer(newValue.value)}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            field.onChange(newValue);
                            setCustomer(newValue.value);
                          } else {
                            field.onChange(null);
                            setCustomer(undefined);
                          }
                        }}
                        options={CustomerOption.map((option: any) => option)}
                        value={field.value}
                        getOptionLabel={(option) => option.text}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              size="small"
                              label={option.value}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('Customer.Customer')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="shopId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        // onChange={(event, newValue) => setShop(newValue.value)}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            field.onChange(newValue);
                            setShop(newValue.value);
                          } else {
                            field.onChange(null);
                            setShop(undefined);
                          }
                        }}
                        options={ShopOption.map((option: any) => option)}
                        value={field.value}
                        getOptionLabel={(option) => option.text}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              size="small"
                              label={option.value}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('Shop.Shop')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />

                  <Button
                    sx={{ width: '120px' }}
                    variant="contained"
                    onClick={Search}
                    startIcon={<Iconify icon="eva:search-fill" />}
                  >
                    {translate('Report.Filter')}
                  </Button>

                  <Controller
                    name="dailyReport"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            {...field}
                            onChange={(e) => {
                              field.onChange(e); // Notify React Hook Form of the value change
                              handleChange(e); // Handle the switch state change
                            }}
                            checked={field.value}
                          />
                        }
                        label={translate('RentPayment.DailyReport')}
                        labelPlacement="end"
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        <>
          {isloading ? (
            <Loader />
          ) : (
            <>
              <Grid item xs={12} md={12} sx={{ mt: 2 }}>
                <Stack sx={{ p: 1 }}>
                  <TableContainer sx={{ maxHeight: 500 }} component={Paper}>
                    <Table stickyHeader sx={{ minWidth: 700 }} aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <TableCell>{translate('GeneralFields.No')}</TableCell>
                          <TableCell>{translate('RentPayment.Year')}</TableCell>
                          <TableCell>{translate('RentPayment.Month')}</TableCell>
                          <TableCell>{translate('RentPayment.CustomerName')}</TableCell>
                          <TableCell>{translate('RentPayment.ShopCode')}</TableCell>
                          <TableCell>{translate('GeneralFields.StartDate')}</TableCell>
                          <TableCell>{translate('GeneralFields.EndDate')}</TableCell>
                          <TableCell>{translate('RentPayment.RecievedByUserName')}</TableCell>
                          <TableCell>{translate('RentPayment.RemainFromThePast')}</TableCell>
                          <TableCell>{translate('RentPayment.CurrentMonthRent')}</TableCell>
                          <TableCell>{translate('RentPayment.PaymentAmount')}</TableCell>
                          {checked ? (
                            <TableCell>{translate('RentPayment.TodaysRecievedAmount')}</TableCell>
                          ) : (
                            ''
                          )}
                          <TableCell>{translate('RentPayment.PaidAmount')}</TableCell>
                          <TableCell>{translate('RentPayment.RemainAmount')}</TableCell>
                          <TableCell>{translate('RentPayment.PaymentDate')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {RentpaymentReportResult?.list.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell component="th" scope="row">
                              {index + 1}
                            </TableCell>
                            <TableCell align="right">
                              {' '}
                              <Label color="warning"> {row.year}</Label>
                            </TableCell>
                            <TableCell align="right">
                              <Label color="success"> {GetMonthName(row.month)}</Label>
                            </TableCell>
                            <TableCell align="right">{row.customerName}</TableCell>
                            <TableCell align="right">{row.shopCode}</TableCell>
                            <TableCell align="right">{<DateConverter date={row.contractStartDate} />}</TableCell>
                            <TableCell align="right">{<DateConverter date={row.contractEndDate} />}</TableCell>

                            <TableCell align="right">{row.recievedByUserName}</TableCell>
                            <TableCell align="right">
                              {row.remnantsOfThePast}{' '}
                              {row.currencyId === 2
                                ? GetCurrencySymbol('afn')
                                : GetCurrencySymbol('dollar')}
                            </TableCell>
                            <TableCell align="right">
                              {row.currentMonthRent}{' '}
                              {row.currencyId === 2
                                ? GetCurrencySymbol('afn')
                                : GetCurrencySymbol('dollar')}
                            </TableCell>
                            <TableCell align="right">
                              {row.paymentAmount}{' '}
                              {row.currencyId === 2
                                ? GetCurrencySymbol('afn')
                                : GetCurrencySymbol('dollar')}
                            </TableCell>
                            {checked ? (
                              <TableCell align="right">
                                {row.todaysRecievedAmount}{' '}
                                {row.currencyId === 2
                                  ? GetCurrencySymbol('afn')
                                  : GetCurrencySymbol('dollar')}
                              </TableCell>
                            ) : (
                              ''
                            )}
                            <TableCell align="right">
                              {row.paidAmount}{' '}
                              {row.currencyId === 2
                                ? GetCurrencySymbol('afn')
                                : GetCurrencySymbol('dollar')}
                            </TableCell>
                            <TableCell align="right">
                              {row.remainAmount === 0 ? (
                                <Label color="warning"> Paid</Label>
                              ) : (
                                `${row.remainAmount}  ${row.currencyId === 2
                                  ? GetCurrencySymbol('afn')
                                  : GetCurrencySymbol('dollar')
                                }`
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {row.paymentDate !== null ? (
                                <DateConverter date={row.paymentDate} />
                              ) : (
                                ''
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </Grid>
              <Grid item xs={12} sx={{ width: '100%', mb: 2, mt: 2 }}>
                <div>
                  <h3 className="text-center text-xl font-bold border-4 border-black p-2">
                    {translate('Shop.Report.ReportSummary')}
                  </h3>
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="report table">
                      <TableHead>
                        <TableRow>
                          {/* <TableCell align="center">{translate('ElectricityPrint.Year')}</TableCell>
                          <TableCell align="center">
                            {translate('ElectricityPrint.Month')}
                          </TableCell> */}
                          <TableCell align="center">{translate('Income.Currency')}</TableCell>
                          <TableCell align="center">
                            {translate('ReportSummary.TotalPaymentAmount')}
                          </TableCell>
                          <TableCell align="center">
                            {translate('ReportSummary.TotalPaidAmount')}
                          </TableCell>
                          <TableCell align="center">
                            {translate('ReportSummary.TotalRemainAmount')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>

                        {flatReport?.map((item, index) => (
                          <TableRow key={index}>




                            <TableCell align="center">{item.currencyName}</TableCell>


                            <TableCell align="center">
                              {item.totalPaymentAmount}{' '}
                              {item.currencyId === 2
                                ? GetCurrencySymbol('afn')
                                : GetCurrencySymbol('dollar')}
                            </TableCell>
                            <TableCell align="center">
                              {item.totalPaidAmount}{' '}
                              {item.currencyId === 2
                                ? GetCurrencySymbol('afn')
                                : GetCurrencySymbol('dollar')}
                            </TableCell>
                            <TableCell align="center">
                              {item.totalRemainAmount}{' '}
                              {item.currencyId === 2
                                ? GetCurrencySymbol('afn')
                                : GetCurrencySymbol('dollar')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </Grid>
            </>
          )}
        </>
      </Container>
    </Page>
  );
});
